import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAppConfig } from '@/hooks/use-app-config';
import { useSubscriptionStatus, useCreateSubscriptionCheckout } from '@/hooks/use-subscription-status';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, BookmarkPlus, Check } from 'lucide-react';
import { ensureApplePay, tryApplePay } from '@/lib/applePay';
import { ensureGooglePay, tryGooglePay } from '@/lib/googlePay';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { useQueryClient } from '@tanstack/react-query';
import { PaymentSuccessAnimation } from './PaymentSuccessAnimation';
import { useAndroidDetection } from '@/hooks/use-android-detection';


interface SubscriptionPurchaseButtonProps {
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  onReadingListToggle?: () => void;
  isInReadingList?: boolean;
  isReadingListLoading?: boolean;
  onOpenAuthModal?: () => void;
}

export function SubscriptionPurchaseButton({ className, size = 'default', onReadingListToggle, isInReadingList, isReadingListLoading, onOpenAuthModal }: SubscriptionPurchaseButtonProps) {
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { toast } = useToast();
  const createCheckout = useCreateSubscriptionCheckout();
  const { country, currency } = useCurrencyDetection();
  const queryClient = useQueryClient();
  const isAndroid = useAndroidDetection();

  // Only show for subscription model and non-active subscriptions
  if (appConfig?.current_model !== 'subscription' || subscriptionStatus?.subscription_status === 'active') {
    return null;
  }

  const handleSubscribe = async () => {
    // Check if user is logged in
    if (!user) {
      // If onOpenAuthModal is provided, use it instead of navigating
      if (onOpenAuthModal) {
        onOpenAuthModal();
      } else {
        navigate('/userflow');
      }
      return;
    }

    // Open global tier selector hosted at the app root
    window.dispatchEvent(new CustomEvent('open-tier-selector', { detail: { onSelect: handleTierSelected } }));
  };

  const handleTierSelected = async (tier: 'monthly' | 'annual', price: number) => {
    console.log('[SUBSCRIBE-BTN] Starting payment flow');

    // Initialize payment methods when user selects tier
    let stripePk: string | null = null;
    let applePayReady = false;
    let googlePayReady = false;

    try {
      const { data: configData } = await supabase.functions.invoke('get-stripe-config');
      if (configData?.publishableKey) {
        stripePk = configData.publishableKey;
        
        // Try to initialize native payment methods
        if (isAndroid) {
          googlePayReady = await ensureGooglePay(
            configData.publishableKey,
            country || 'GB',
            currency.code.toLowerCase()
          );
        } else {
          applePayReady = await ensureApplePay(
            configData.publishableKey,
            country || 'GB',
            currency.code.toLowerCase()
          );
        }
      }
    } catch (error) {
      console.error('[SUBSCRIBE-BTN] Payment init error:', error);
    }

    // Try native payment if available
    if (isAndroid && googlePayReady && stripePk) {
      try {
        const success = await tryGooglePay(
          stripePk,
          user!.email!,
          tier,
          subscriptionStatus?.has_had_subscription ? (appConfig?.monthly_price || 9.99) : 0,
          currency.code,
          subscriptionStatus?.has_had_subscription || false,
          appConfig?.monthly_price || 9.99,
          async () => {
            const { data, error } = await supabase.functions.invoke('create-setup-intent', {
              body: { email: user!.email }
            });
            if (error || !data?.client_secret) throw error || new Error('No client secret');
            return data;
          },
          async (paymentMethod: string) => {
            const { error } = await supabase.functions.invoke('start-trial-subscription', {
              body: { 
                payment_method: paymentMethod, 
                tier,
                currency: currency.code.toLowerCase()
              }
            });
            if (error) throw error;
          }
        );
        
        if (success) {
          setShowSuccessAnimation(true);
          await queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
          await queryClient.refetchQueries({ queryKey: ['subscriptionStatus'] });
          return;
        }
      } catch (error: any) {
        console.error('[SUBSCRIBE-BTN] Google Pay error:', error);
      }
    } else if (applePayReady && stripePk) {
      try {
        const success = await tryApplePay(
          stripePk,
          user!.email!,
          tier,
          subscriptionStatus?.has_had_subscription ? (appConfig?.monthly_price || 9.99) : 0,
          currency.code,
          subscriptionStatus?.has_had_subscription || false,
          appConfig?.monthly_price || 9.99,
          async () => {
            const { data, error } = await supabase.functions.invoke('create-setup-intent', {
              body: { email: user!.email }
            });
            if (error || !data?.client_secret) throw error || new Error('No client secret');
            return data;
          },
          async (paymentMethod: string) => {
            const { error } = await supabase.functions.invoke('start-trial-subscription', {
              body: { 
                payment_method: paymentMethod, 
                tier,
                currency: currency.code.toLowerCase()
              }
            });
            if (error) throw error;
          }
        );
        
        if (success) {
          setShowSuccessAnimation(true);
          await queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
          await queryClient.refetchQueries({ queryKey: ['subscriptionStatus'] });
          return;
        }
      } catch (error: any) {
        console.error('[SUBSCRIBE-BTN] Apple Pay error:', error);
      }
    }

    // Fallback to Stripe checkout
    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: { tier, currency: currency.code.toLowerCase() }
      });
      
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('[SUBSCRIBE-BTN] Checkout error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start subscription. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      {showSuccessAnimation && (
        <PaymentSuccessAnimation 
          onComplete={() => {
            setShowSuccessAnimation(false);
            toast({
              title: "Success! 🎉",
              description: "Your subscription is now active!",
            });
          }}
        />
      )}
      
      <div className="flex gap-2 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
        {/* Only show price for logged-in users - we want logged-out users to sign up first */}
        {user && (
          <p className="text-white/80 text-[11px] text-center">
            from {currency.symbol}{((appConfig?.annual_price || 47.88) / 12).toFixed(2)} per month
          </p>
        )}
          
          <Button
            onClick={handleSubscribe}
            className={`min-w-[160px] md:min-w-[180px] h-10 md:h-12 px-5 md:px-10 bg-white text-black font-semibold text-sm md:text-base hover:bg-white/90 transition-all duration-200 rounded-[10px] ${className || ''}`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif'
            }}
            size="lg"
          >
            {!user ? "Get Started" : subscriptionStatus?.has_had_subscription ? "Renew Subscription" : "Subscribe Now"}
          </Button>
        </div>
        
        {onReadingListToggle && (
          <Button
            onClick={onReadingListToggle}
            disabled={isReadingListLoading}
            className="h-10 w-10 md:h-12 md:w-12 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20 font-semibold rounded-full transition-all duration-200 flex items-center justify-center flex-shrink-0 self-end"
          >
            {isReadingListLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
            ) : isInReadingList ? (
              <Check className="w-5 h-5" />
            ) : (
              <BookmarkPlus className="w-5 h-5" />
            )}
          </Button>
        )}
      </div>
    </>
  );
}